import { useState } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { runCombinedCompute, downloadCombinedCompute, loadFromFtp, CATEGORIES } from "../lib/api";
import { Zap, Download, Loader2, AlertTriangle, FolderOpen, RefreshCcw, FileCheck, CloudDownload } from "lucide-react";

export default function ExecutionCenter({ csvInfoByCategory, onReload, vatRate, minQty, fallbackTier }) {
  const [computing, setComputing] = useState(false);
  const [result, setResult] = useState(null);

  const handleCompute = async () => {
    setComputing(true);
    setResult(null);
    try {
      const data = await runCombinedCompute({
        vat_rate: vatRate,
        min_qty: minQty,
        fallback_tier: fallbackTier,
      });
      setResult(data);
      const total = Object.values(data.stats_by_category).reduce((sum, s) => sum + s.output_count, 0);
      toast.success(`Computed ${total.toLocaleString()} rows`);
      if (data.skipped?.length) {
        data.skipped.forEach((s) => toast.warning(`${categoryLabel(s.category)} skipped: ${s.reason}`));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Compute failed");
    } finally {
      setComputing(false);
    }
  };

  const handleDownload = async () => {
    try {
      await downloadCombinedCompute();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    }
  };

  return (
    <div className="space-y-6">
      {CATEGORIES.map((c, idx) => (
        <SourcePanel
          key={c.value}
          stepNumber={idx + 1}
          category={c.value}
          categoryLabel={c.label}
          csvInfo={csvInfoByCategory[c.value] || null}
          onReload={onReload}
          minQty={minQty}
        />
      ))}

      {/* Execute */}
      <section className="border border-[#E2E8F0] bg-white sharp">
        <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3">
          <div className="eyebrow text-[#64748B]">{CATEGORIES.length + 1} / Execute</div>
          <div className="text-xs text-[#64748B] mt-1">Computes every category that has a CSV loaded and a completed Data Mapping, and combines them into one output.</div>
        </div>
        <div className="p-6">
          <div className="flex items-center gap-3 flex-wrap">
            <Button
              size="lg"
              disabled={computing}
              onClick={handleCompute}
              className="rounded-sm h-11 px-6 bg-[#1A2340] hover:bg-[#253060] text-white font-bold gap-2 disabled:opacity-50"
              data-testid="compute-pricing-button"
            >
              {computing ? (
                <>
                  <Loader2 size={16} className="animate-spin" /> Processing…
                </>
              ) : (
                <>
                  <Zap size={16} className="fill-[#F5C800] text-[#F5C800]" /> Compute Pricing
                </>
              )}
            </Button>

            {result?.output_rows?.length > 0 && (
              <Button
                size="lg"
                onClick={handleDownload}
                className="rounded-sm h-11 px-6 bg-[#F5C800] hover:bg-[#DDB400] text-[#1A2340] font-bold gap-2"
                data-testid="download-output-button"
              >
                <Download size={16} /> Download CSV
              </Button>
            )}
          </div>

          {computing && (
            <div className="mt-5 h-1.5 bg-[#F1F5F9] overflow-hidden">
              <div className="h-full bg-[#F5C800] w-1/3 pulse-bar" />
            </div>
          )}

          {result?.skipped?.length > 0 && (
            <div className="mt-4 p-3 border border-amber-200 bg-amber-50/50 text-xs text-amber-800">
              {result.skipped.map((s) => (
                <div key={s.category}>⚠ {categoryLabel(s.category)} skipped — {s.reason}</div>
              ))}
            </div>
          )}

          {result?.stats_by_category && (
            <div className="mt-6 space-y-5">
              {Object.entries(result.stats_by_category).map(([cat, stats]) => (
                <div key={cat}>
                  <div className="eyebrow text-[#64748B] mb-2">{categoryLabel(cat)}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <ResultStat label="Output Rows" value={stats.output_count} accent dataTestId={`stat-output-${cat}`} />
                    <ResultStat label="Rows Dropped" value={stats.dropped} dataTestId={`stat-dropped-${cat}`} />
                    <ResultStat label="Fallback Tier" value={stats.fallback_count} dataTestId={`stat-fallback-${cat}`} />
                    <ResultStat label="Unmatched Brands" value={stats.unmatched_count} dataTestId={`stat-unmatched-${cat}`} />
                  </div>
                  {stats.unmatched_brands?.length > 0 && (
                    <div className="mt-3 p-3 border border-amber-200 bg-amber-50/50">
                      <div className="flex flex-wrap gap-1.5">
                        {stats.unmatched_brands.map((b) => (
                          <span key={b} className="font-mono-data text-[11px] px-2 py-0.5 bg-white border border-amber-300 text-amber-900">{b}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {result?.preview?.length > 0 && (() => {
            // Different rows can have different optional columns mapped, so build
            // the header set from the union of all preview rows, in first-seen order.
            const previewColumns = [];
            const seenCols = new Set();
            result.preview.forEach((r) => {
              Object.keys(r).forEach((k) => {
                if (!seenCols.has(k)) {
                  seenCols.add(k);
                  previewColumns.push(k);
                }
              });
            });
            return (
              <div className="mt-6 border border-[#E2E8F0]">
                <div className="bg-[#F8F9FA] px-4 py-2 eyebrow text-[#64748B] border-b border-[#E2E8F0]">
                  Combined Preview — First {result.preview.length} rows
                </div>
                <div className="overflow-auto max-h-[420px]">
                  <table className="tbl">
                    <thead>
                      <tr>
                        {previewColumns.map((k) => (
                          <th key={k}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.preview.map((r, i) => (
                        <tr key={i}>
                          {previewColumns.map((k) => (
                            <td key={k} className={typeof r[k] === "number" ? "num" : ""}>
                              {r[k] === undefined ? "" : String(r[k])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}
        </div>
      </section>
    </div>
  );
}

function categoryLabel(value) {
  return CATEGORIES.find((c) => c.value === value)?.label || value;
}

function SourcePanel({ stepNumber, category, categoryLabel: label, csvInfo, onReload, minQty }) {
  const [loadingFtp, setLoadingFtp] = useState(false);
  const loaded = !!csvInfo?.loaded;

  const handleLoadData = async () => {
    setLoadingFtp(true);
    try {
      const data = await loadFromFtp(category);
      toast.success(`Loaded ${data.filename} · ${data.row_count.toLocaleString()} rows`);
      await onReload(category, { silent: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load data from FTP");
    } finally {
      setLoadingFtp(false);
    }
  };

  return (
    <section className="border border-[#E2E8F0] bg-white sharp">
      <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
        <div className="eyebrow text-[#64748B]">{stepNumber} / Source File — {label}</div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            disabled={loadingFtp}
            className="rounded-sm h-8 text-xs gap-2 bg-[#1A2340] hover:bg-[#253060] text-white disabled:opacity-50"
            onClick={handleLoadData}
            data-testid={`load-data-ftp-button-${category}`}
          >
            {loadingFtp ? (
              <>
                <Loader2 size={13} className="animate-spin" /> Loading…
              </>
            ) : (
              <>
                <CloudDownload size={13} /> Load Data
              </>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm h-8 text-xs gap-2 border-[#1A2340]/20 hover:bg-[#1A2340] hover:text-white"
            onClick={() => onReload(category)}
            data-testid={`reload-csv-button-${category}`}
          >
            <RefreshCcw size={13} /> Rescan Folder
          </Button>
        </div>
      </div>
      <div className="p-6">
        {loaded ? (
          <>
            <div className="flex items-center gap-5 flex-wrap" data-testid={`csv-loaded-panel-${category}`}>
              <div className="w-14 h-14 bg-[#1A2340] flex items-center justify-center sharp">
                <FileCheck size={26} className="text-[#F5C800]" />
              </div>
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono-data text-[15px] font-semibold text-[#1A2340]" data-testid={`csv-filename-${category}`}>{csvInfo.filename}</div>
                <div className="text-xs text-[#64748B] mt-0.5 flex gap-4 flex-wrap">
                  <span>📂 {csvInfo.folder}</span>
                  <span>· Modified: <span className="font-mono-data">{new Date(csvInfo.modified).toLocaleString()}</span></span>
                </div>
              </div>
              <div className="flex gap-6 ml-auto flex-wrap">
                <Stat label="Rows" value={csvInfo.row_count?.toLocaleString()} />
                {csvInfo.above_threshold !== undefined && (
                  <Stat label="Above Threshold" value={csvInfo.above_threshold?.toLocaleString()} />
                )}
                {csvInfo.below_threshold !== undefined && (
                  <Stat label="Below Threshold" value={csvInfo.below_threshold?.toLocaleString()} warn={csvInfo.below_threshold > 0} />
                )}
                <Stat label="Columns" value={csvInfo.headers?.length} />
                <Stat label="Brands" value={csvInfo.brands?.length} accent />
              </div>
            </div>
            {csvInfo.above_threshold === undefined && (
              <div className="mt-4 text-xs text-[#64748B]">
                Map the <span className="font-semibold">Quantity</span> column in Data Mapping to see Above/Below Threshold counts (Min Quantity Threshold: {minQty}).
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-4 p-6 border-2 border-dashed border-amber-300 bg-amber-50/40">
            <AlertTriangle className="text-amber-600" />
            <div>
              <div className="font-semibold text-[#1A2340]">No CSV detected for {label}</div>
              <div className="text-xs text-[#64748B] mt-1 flex items-center gap-1 flex-wrap">
                <FolderOpen size={12} /> Click <span className="font-semibold">Load Data</span> to fetch
                <span className="font-mono-data px-1.5 bg-white border">{csvInfo?.expected_filename || "the file"}</span>
                via FTP, or drop it manually into
                <span className="font-mono-data px-1.5 bg-white border">{csvInfo?.folder || "/app/data"}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, accent = false, warn = false }) {
  return (
    <div className="text-right">
      <div className={`font-display font-black text-2xl leading-none ${warn ? "text-[#E11D48]" : accent ? "text-[#1A2340]" : "text-[#1A2340]"}`}>
        {value ?? "—"}
      </div>
      <div className="eyebrow text-[#64748B] mt-1">{label}</div>
    </div>
  );
}

function ResultStat({ label, value, accent = false, dataTestId }) {
  return (
    <div className={`p-4 border ${accent ? "border-[#F5C800] bg-[#FFFBEB]" : "border-[#E2E8F0] bg-[#F8F9FA]"}`} data-testid={dataTestId}>
      <div className="font-display font-black text-3xl text-[#1A2340] leading-none">{(value ?? 0).toLocaleString()}</div>
      <div className="eyebrow text-[#64748B] mt-2">{label}</div>
    </div>
  );
}
